import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import {
  Video, Upload, Trash2, Play, Users, Check, X, Film, HardDrive,
  Plus, Search, Eye, EyeOff, FileVideo, Loader2
} from "lucide-react";
import { API } from "../App";
import { useI18n } from "../lib/i18n";

const formatFileSize = (bytes) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
};

const TherapistMeditationsPage = ({ user, logout }) => {
  const { t } = useI18n();
  const fileInputRef = useRef(null);
  const sessionToken = localStorage.getItem("session_token") || "";
  const [meditations, setMeditations] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState(""); // "chunks" | "assembling"
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadLang, setUploadLang] = useState("fr");
  const [selectedFile, setSelectedFile] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedMeditation, setSelectedMeditation] = useState(null);
  const [patientAssignments, setPatientAssignments] = useState({});
  const [previewId, setPreviewId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [searchPatient, setSearchPatient] = useState("");

  useEffect(() => {
    fetchMeditations();
    fetchPatients();
  }, []);

  const fetchMeditations = async () => {
    try {
      const res = await axios.get(`${API}/meditations/library`, { withCredentials: true });
      setMeditations(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const res = await axios.get(`${API}/therapist/patients`, { withCredentials: true });
      setPatients(res.data.filter(p => p.is_active));
    } catch (e) {
      console.error(e);
    }
  };

  const togglePreview = async (meditationId) => {
    if (previewId === meditationId) {
      setPreviewId(null);
      setPreviewUrl(null);
      return;
    }
    setPreviewId(meditationId);
    setPreviewUrl(null);
    try {
      const res = await axios.get(`${API}/meditations/${meditationId}/stream?token=${sessionToken}`, { withCredentials: true });
      setPreviewUrl(res.data.stream_url);
    } catch (e) {
      toast.error(t("meditations.streamError", "Erreur de lecture video"));
      setPreviewId(null);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    const allowed = ["mp4", "mov", "avi", "webm", "mkv"];
    if (!allowed.includes(ext)) {
      toast.error(t("meditations.invalidFormat", "Format non supporté. Utilisez MP4, MOV, AVI, WebM ou MKV."));
      return;
    }
    if (file.size > 800 * 1024 * 1024) {
      toast.error(t("meditations.fileTooLarge", "Fichier trop volumineux (max 800 Mo)"));
      return;
    }
    setSelectedFile(file);
    if (!uploadTitle) {
      setUploadTitle(file.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, " "));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadTitle.trim()) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadPhase("chunks");

    const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks for faster uploads

    try {
      // Step 1: Init upload session
      const initRes = await axios.post(`${API}/meditations/init-upload`, {
        title: uploadTitle.trim(),
        description: uploadDesc.trim(),
        language: uploadLang,
        filename: selectedFile.name,
        file_size: selectedFile.size,
      }, { withCredentials: true });

      const { meditation_id } = initRes.data;
      const totalChunks = Math.ceil(selectedFile.size / CHUNK_SIZE);

      // Step 2: Upload chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, selectedFile.size);
        const chunk = selectedFile.slice(start, end);

        const chunkForm = new FormData();
        chunkForm.append("chunk_index", i.toString());
        chunkForm.append("file", chunk, `chunk_${i}`);

        await axios.post(`${API}/meditations/${meditation_id}/chunk`, chunkForm, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });

        setUploadProgress(Math.round(((i + 1) / totalChunks) * 90)); // 0-90% for chunks
      }

      // Step 3: Complete upload (assemble on server + upload to GCS)
      setUploadPhase("assembling");
      setUploadProgress(95);
      await axios.post(`${API}/meditations/complete-upload`, {
        meditation_id,
      }, { withCredentials: true, timeout: 900000 }); // 15 minutes for large files

      setUploadProgress(100);
      toast.success(t("meditations.uploadSuccess", "Video uploadee avec succes !"));
      setSelectedFile(null);
      setUploadTitle("");
      setUploadDesc("");
      setUploadProgress(0);
      setUploadPhase("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchMeditations();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("meditations.uploadError", "Erreur lors de l'upload"));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (meditationId) => {
    if (!window.confirm(t("meditations.confirmDelete", "Supprimer cette meditation ?"))) return;
    try {
      await axios.delete(`${API}/meditations/${meditationId}`, { withCredentials: true });
      toast.success(t("meditations.deleted", "Meditation supprimee"));
      setMeditations(prev => prev.filter(m => m.meditation_id !== meditationId));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const openAssignDialog = async (meditation) => {
    setSelectedMeditation(meditation);
    setAssignDialogOpen(true);
    setSearchPatient("");
    // Fetch current assignments for this meditation
    const assignments = {};
    for (const patient of patients) {
      try {
        const res = await axios.get(`${API}/meditations/patient/${patient.user_id}/assignments`, { withCredentials: true });
        const isAssigned = res.data.some(a => a.meditation_id === meditation.meditation_id);
        if (isAssigned) {
          const match = res.data.find(a => a.meditation_id === meditation.meditation_id);
          assignments[patient.user_id] = match.assignment_id;
        }
      } catch {}
    }
    setPatientAssignments(assignments);
  };

  const toggleAssignment = async (patientId) => {
    if (!selectedMeditation) return;
    const isAssigned = !!patientAssignments[patientId];

    try {
      if (isAssigned) {
        await axios.delete(`${API}/meditations/assign/${patientAssignments[patientId]}`, { withCredentials: true });
        setPatientAssignments(prev => {
          const next = { ...prev };
          delete next[patientId];
          return next;
        });
        toast.success(t("meditations.unassigned", "Meditation retiree"));
      } else {
        const res = await axios.post(`${API}/meditations/assign`, {
          meditation_id: selectedMeditation.meditation_id,
          patient_id: patientId,
        }, { withCredentials: true });
        setPatientAssignments(prev => ({ ...prev, [patientId]: res.data.assignment_id }));
        toast.success(t("meditations.assigned", "Meditation assignee"));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const filteredPatients = patients.filter(p =>
    p.name?.toLowerCase().includes(searchPatient.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchPatient.toLowerCase())
  );

  const LANG_OPTIONS = [
    { value: "fr", label: "Francais" },
    { value: "en", label: "English" },
    { value: "es", label: "Espanol" },
  ];

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="therapist-meditations-page">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("meditations.title", "Meditations Video")}</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("meditations.subtitle", "Uploadez et gerez vos videos de meditation guidee pour vos patients")}</p>
        </div>

        {/* Upload Section */}
        <Card data-testid="upload-section">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Upload className="w-5 h-5" />
              {t("meditations.uploadNew", "Uploader une video")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                selectedFile ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
              }`}
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,video/x-matroska,.mp4,.mov,.avi,.webm,.mkv"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              {selectedFile ? (
                <div className="flex items-center justify-center gap-3">
                  <FileVideo className="w-8 h-8 text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div>
                  <Video className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">{t("meditations.dropzoneText", "Cliquez pour selectionner une video")}</p>
                  <p className="text-xs text-muted-foreground text-sm sm:text-base mt-1">MP4, MOV, AVI, WebM, MKV - Max 800 Mo</p>
                </div>
              )}
            </div>

            {selectedFile && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t("meditations.videoTitle", "Titre de la video")}</Label>
                  <Input
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    placeholder={t("meditations.titlePlaceholder", "Ex: Respiration guidee 10min")}
                    data-testid="upload-title-input"
                  />
                </div>
                <div>
                  <Label>{t("meditations.language", "Langue")}</Label>
                  <select
                    value={uploadLang}
                    onChange={(e) => setUploadLang(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="upload-lang-select"
                  >
                    {LANG_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <Label>{t("meditations.description", "Description (optionnel)")}</Label>
                  <Input
                    value={uploadDesc}
                    onChange={(e) => setUploadDesc(e.target.value)}
                    placeholder={t("meditations.descPlaceholder", "Description de la meditation...")}
                    data-testid="upload-desc-input"
                  />
                </div>
              </div>
            )}

            {uploading && (
              <div className="space-y-2" data-testid="upload-progress">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {uploadPhase === "assembling"
                      ? t("meditations.assembling", "Finalisation en cours...")
                      : t("meditations.uploading", "Upload en cours...")}
                  </span>
                  <span className="font-medium">{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-2" />
                {uploadPhase === "chunks" && (
                  <p className="text-xs text-muted-foreground">{t("meditations.chunkInfo", "Envoi par morceaux pour supporter les gros fichiers")}</p>
                )}
              </div>
            )}

            {selectedFile && !uploading && (
              <Button
                onClick={handleUpload}
                disabled={!uploadTitle.trim()}
                className="w-full gap-2"
                data-testid="upload-submit-btn"
              >
                <Upload className="w-4 h-4" />
                {t("meditations.uploadBtn", "Uploader la video")}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Video Library */}
        <Card data-testid="video-library">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Film className="w-5 h-5" />
              {t("meditations.library", "Bibliotheque de videos")}
              {meditations.length > 0 && (
                <span className="text-sm font-normal text-muted-foreground ml-2">({meditations.length})</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse flex items-center gap-4 p-4 rounded-lg border">
                    <div className="w-16 h-16 bg-muted rounded-lg" />
                    <div className="flex-1"><div className="h-4 bg-muted rounded w-40 mb-2" /><div className="h-3 bg-muted rounded w-24" /></div>
                  </div>
                ))}
              </div>
            ) : meditations.length > 0 ? (
              <div className="space-y-3">
                {meditations.map(med => (
                  <div
                    key={med.meditation_id}
                    className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                    data-testid={`meditation-item-${med.meditation_id}`}
                  >
                    <div
                      className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 cursor-pointer hover:bg-primary/20 transition-colors"
                      onClick={() => togglePreview(med.meditation_id)}
                    >
                      {previewId === med.meditation_id ? (
                        <EyeOff className="w-6 h-6 text-primary" />
                      ) : (
                        <Play className="w-6 h-6 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate">{med.title}</h3>
                      {med.description && <p className="text-sm text-muted-foreground truncate">{med.description}</p>}
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><HardDrive className="w-3 h-3" />{formatFileSize(med.size)}</span>
                        <span className="uppercase">{med.language}</span>
                        <span>{new Date(med.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => openAssignDialog(med)}
                        data-testid={`assign-btn-${med.meditation_id}`}
                      >
                        <Users className="w-4 h-4" />
                        {t("meditations.assign", "Assigner")}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10"
                        onClick={() => handleDelete(med.meditation_id)}
                        data-testid={`delete-btn-${med.meditation_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {/* Inline video preview */}
                    {previewId === med.meditation_id && (
                      <div className="w-full mt-2" data-testid={`preview-${med.meditation_id}`}>
                        {previewUrl ? (
                          <video
                            controls
                            className="w-full max-h-[400px] rounded-lg bg-black"
                            src={previewUrl}
                          >
                            {t("meditations.noSupport", "Votre navigateur ne supporte pas la lecture video.")}
                          </video>
                        ) : (
                          <div className="flex items-center justify-center h-32 bg-muted rounded-lg">
                            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Video className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{t("meditations.emptyLibrary", "Aucune video uploadee")}</p>
                <p className="text-sm text-muted-foreground text-sm sm:text-base mt-1">{t("meditations.emptyLibraryHint", "Uploadez votre premiere video de meditation ci-dessus")}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Assign Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="max-w-md" data-testid="assign-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Users className="w-5 h-5" />
                {t("meditations.assignTo", "Assigner a des patients")}
              </DialogTitle>
            </DialogHeader>
            {selectedMeditation && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {t("meditations.assignHint", "Selectionnez les patients qui auront acces a")} <strong>{selectedMeditation.title}</strong>
                </p>
                {patients.length > 3 && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder={t("meditations.searchPatient", "Rechercher un patient...")}
                      value={searchPatient}
                      onChange={(e) => setSearchPatient(e.target.value)}
                      className="pl-9"
                      data-testid="search-patient-input"
                    />
                  </div>
                )}
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {filteredPatients.length > 0 ? filteredPatients.map(patient => {
                    const isAssigned = !!patientAssignments[patient.user_id];
                    return (
                      <div
                        key={patient.user_id}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                          isAssigned ? "bg-primary/5 border-primary/30" : "hover:bg-muted"
                        }`}
                        onClick={() => toggleAssignment(patient.user_id)}
                        data-testid={`patient-assign-${patient.user_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-primary font-medium text-sm">{patient.name?.charAt(0)?.toUpperCase()}</span>
                          </div>
                          <div>
                            <p className="font-medium text-sm">{patient.name}</p>
                            <p className="text-xs text-muted-foreground">{patient.email}</p>
                          </div>
                        </div>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          isAssigned ? "bg-primary text-white" : "border-2 border-muted-foreground/30"
                        }`}>
                          {isAssigned && <Check className="w-4 h-4" />}
                        </div>
                      </div>
                    );
                  }) : (
                    <p className="text-sm text-muted-foreground text-center py-4">{t("meditations.noPatients", "Aucun patient actif")}</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TherapistMeditationsPage;
